import { generateCountryGroupOptionsList } from '~/src/dash/api/country-group';
import { generateAudienceSegmentOptionsList } from '~/src/dash/apiV2/audienceSegments';
import {
  generateChannelGroupsOptionsList,
  generateChannelsOptionsList,
} from '~/src/dash/apiV2/channels';
import { generateContextualSegmentOptionsList } from '~/src/dash/apiV2/contextualSegments';
import {
  InventoryParametersKey,
  InventoryParametersKeys,
} from '~/src/dash/apiV2/forecasting/forecasting';
import {
  generateOptionsListAsPromise,
  ISelectOption,
} from '~/src/dash/components/dropdowns/common';
import {
  convertDaypartingUI,
  convertDevicesResponseValue,
  convertDevicesSelectedValue,
  convertDeviceSubTypesResponseValue,
  convertDeviceSubTypesSelectedValue,
  convertDeviceModelsResponseValue,
  convertDeviceModelsSelectedValue,
  convertDeviceTypesResponseValue,
  convertDeviceTypesSelectedValue,
  convertLivestreamResponseValue,
  convertLivestreamSelectedValue,
  // convertPodSequenceTargetingSelectedValue,
  // convertPositionTargetingSelectedValue,
  // convertSlotsResponseValue,
  // convertSlotsSelectedValue,
  // convertStringOrNumberResponseValue,
  generateContentPlaceholder,
  generateCustomPlaceholder,
  generateGenericPlaceholder,
  parseDaypartingForUI,
  validateTextInput,
} from '~/src/dash/components/forms/formBuilder/common';
import {
  AppBundleIDsSitePagesEditor,
  IAppBundleIDsSitePagesEditorProps,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/AppBundleIDsSitePagesEditor';
import {
  convertSetIntoSingleNumber,
  convertSetIntoString,
  convertSingleNumberIntoArray,
  convertStringIntoArray,
  removeEmojiPrefixFromLabel,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/common/common';
import {
  DaypartingBuilder,
  IDaypartingBuilderProps,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/DaypartingBuilder';
import { GDPRSelect } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/GDPRSelect';
import { RegionsEditor } from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/RegionsEditor';
import {
  ISelectWithIncludeAndLogicProps,
  SelectWithIncludeAndLogic,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/SelectWithIncludeAndLogic';
import {
  ITextInputWithIncludeProps,
  TextInputWithInclude,
} from '~/src/dash/components/forms/formBuilder/ParameterRow/editors/TextInputWithInclude';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { generateDMAOptions } from '~/src/dash/utils/countries';
import {
  DeviceOptions,
  DeviceORTBTypeOptions,
  generateCountryOptions,
  generateDeviceModelsOptionsList,
  generateDeviceSubTypeOptionsList,
  PodSequenceTargetingOptions,
  PositionTargetingOptions,
  SlotOptions,
  TargetBoolOptions,
  TargetingCCPAOptions,
  TargetingCOPPAOptions,
  TargetingGDPRConsentOptions,
  TargetingGDPROptions,
  TargetingLiveStreamOptions,
} from '~/src/dash/utils/options';
import { isPublisherSamsung } from '~/src/dash/utils/publishers';
// import {
//   IPodDurationMinMaxProps,
//   PodDurationMinMax,
// } from '~/src/dash/views/tools/Forecasting/InventoryParameters/PodDurationMinMax';
import {
  TargetingPresetCategory,
  TargetingPresetCategoryKeys,
  TargetingPresetParamKey,
  TargetingPresetParamKeys,
  TargetingPresetSelectCategory,
} from '~/src/dash/views/tools/TargetingPresets/TargetingPresetFormView/config';
import {
  ForecastingLocalTargetingBuilder,
  IForecastingLocalTargetingBuilderProps,
} from '~/src/dash/views/tools/Forecasting/InventoryParameters/ForecastingLocalTargetingBuilder';
import { ct } from '~/src/locales/common';

export type InventoryParametersSelectCategory =
  | 'Channels'
  | 'Deals'
  | 'Inventory'
  | TargetingPresetSelectCategory;

export type InventoryParametersCategory =
  | 'Channels'
  // 'AdBreak' |
  | TargetingPresetCategory;

export const InventoryParametersCategoryKeys: Record<string, InventoryParametersCategory> = {
  CHANNELS: 'Channels',
  // AD_BREAK: 'AdBreak',
  ...TargetingPresetCategoryKeys,
};

export type InventoryParametersParamKey =
  // INVENTORY
  | 'SiteIDs'
  | 'ChannelGroupIDs'
  // | 'Slots'
  // | 'Position'
  // | 'PodSequence'
  // | 'PodDuration'
  // DEALS
  | 'DealID'
  | 'FreewheelSectionIDs'
  | TargetingPresetParamKey;

export const InventoryParametersParamKeys: Record<string, InventoryParametersParamKey> = {
  // INVENTORY
  SITE_IDS: 'SiteIDs',
  CHANNEL_GROUP_IDS: 'ChannelGroupIDs',
  // SLOTS: 'Slots',
  // POSITION: 'Position',
  // POD_SEQUENCE: 'PodSequence',
  // POD_DURATION: 'PodDuration',
  // DEALS
  DEAL_ID: 'DealID',
  FREEWHEEL_SECTION_IDS: 'FreewheelSectionIDs',
  ...TargetingPresetParamKeys,
};

const customTooltipMessage = `Checks for custom parameters set in your request to Publica's endpoint and includes or excludes the bidder. Can be comma separated.`;

interface IConvertedPayload {
  value: any;
  errors: string[];
}

interface ICustomInventoryParametersConversion {
  convertResponse: (value: any, session: ISessionState) => any;
  convertPayload: (value: any) => IConvertedPayload;
  uiKey: InventoryParametersKey;
  apiKey: InventoryParametersKey;
}

interface ICustomValidation {
  validateKey: InventoryParametersKey;
  validateFn: (value: any, parameterLabel: string) => string[];
}

export interface IInventoryParametersParam {
  key: InventoryParametersParamKey;
  label: string; // displayed title
  categoryKey: InventoryParametersCategory; // section category
  selectCategoryKey?: InventoryParametersSelectCategory; // dropdown category
  isSuperUserOnly?: boolean; // make sure to also populate checkIfHidden
  checkIfHidden?: (publisherID: string, isSuperUser: boolean) => boolean; // check if publisher specific
  Component: (props: any) => JSX.Element;
  props?: Record<string, any>;
  apiKeys: InventoryParametersKey[]; // DO NOT INCLUDE KEYS THAT ARE USED IN CUSTOMUICONVERSIONS! IMPORTANT!!!
  customUIConversions?: ICustomInventoryParametersConversion;
  customValidation?: ICustomValidation;
}

export const InventoryParametersParams: Record<
  InventoryParametersParamKey,
  IInventoryParametersParam
> = {
  // CHANNEL CATEGORY
  SiteIDs: {
    key: InventoryParametersParamKeys.SITE_IDS,
    label: 'Channels',
    categoryKey: InventoryParametersCategoryKeys.CHANNELS,
    selectCategoryKey: 'Channels',
    apiKeys: [InventoryParametersKeys.SITE_IDS],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Channels',
      singularLabel: 'Channel',
      listLabel: 'Channels',
      listKey: 'Channels',
      valueKey: InventoryParametersKeys.SITE_IDS,
      key: InventoryParametersKeys.SITE_IDS,
    } as ISelectWithIncludeAndLogicProps,
  },
  ChannelGroupIDs: {
    key: InventoryParametersParamKeys.CHANNEL_GROUP_IDS,
    label: 'Channel Groups',
    categoryKey: InventoryParametersCategoryKeys.CHANNELS,
    selectCategoryKey: 'Channels',
    apiKeys: [InventoryParametersKeys.CHANNEL_GROUP_IDS],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Channel Groups',
      singularLabel: 'Channel Group',
      listLabel: 'Channel Groups',
      listKey: 'ChannelGroups',
      valueKey: InventoryParametersKeys.CHANNEL_GROUP_IDS,
      key: InventoryParametersKeys.CHANNEL_GROUP_IDS,
    } as ISelectWithIncludeAndLogicProps,
  },
  // SUPPLY CATEGORY
  // DEVICE
  TargetedDevices: {
    key: InventoryParametersParamKeys.TARGETED_DEVICES,
    label: 'Targeted Devices',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [
      InventoryParametersKeys.TARGETING_DEVICES,
      InventoryParametersKeys.TARGETING_DEVICES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Devices Selected',
      singularLabel: 'Device',
      listLabel: 'Devices',
      listKey: 'Devices',
      valueKey: InventoryParametersKeys.TARGETING_DEVICES,
      allowKey: InventoryParametersKeys.TARGETING_DEVICES_IS_ALLOWED,
      convertResponseValue: convertDevicesResponseValue,
      convertSelectedValue: convertDevicesSelectedValue,
      key: InventoryParametersKeys.TARGETING_DEVICES,
    } as ISelectWithIncludeAndLogicProps,
  },
  TargetedDeviceTypes: {
    key: InventoryParametersParamKeys.TARGETED_DEVICE_TYPES,
    label: 'Targeted Device Types',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [
      InventoryParametersKeys.TARGETING_DEVICE_TYPES,
      InventoryParametersKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device Types Selected',
      singularLabel: 'Device Type',
      listLabel: 'Device Types',
      listKey: 'DeviceTypes',
      valueKey: InventoryParametersKeys.TARGETING_DEVICE_TYPES,
      allowKey: InventoryParametersKeys.TARGETING_DEVICE_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceTypesResponseValue,
      convertSelectedValue: convertDeviceTypesSelectedValue,
      key: InventoryParametersKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
      isSelectionRequired: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  TargetedDeviceSubtypes: {
    key: InventoryParametersParamKeys.TARGETED_DEVICE_SUBTYPES,
    label: 'Targeted Device Subtypes',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [
      InventoryParametersKeys.TARGETING_DEVICE_SUB_TYPES,
      InventoryParametersKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Device SubTypes Selected',
      singularLabel: 'Device SubType',
      listLabel: 'Device SubTypes',
      listKey: 'DeviceSubTypes',
      valueKey: InventoryParametersKeys.TARGETING_DEVICE_SUB_TYPES,
      allowKey: InventoryParametersKeys.TARGETING_DEVICE_SUB_TYPES_IS_ALLOWED,
      convertResponseValue: convertDeviceSubTypesResponseValue,
      convertSelectedValue: convertDeviceSubTypesSelectedValue,
      key: InventoryParametersKeys.TARGETING_DEVICE_TYPES,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    checkIfHidden: (publisherID) => !isPublisherSamsung(publisherID),
  },
  TargetedDeviceModels: {
    key: InventoryParametersParamKeys.TARGETED_DEVICE_MODELS,
    label: ct('TargetedDeviceModels'),
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [
      InventoryParametersKeys.TARGETING_DEVICE_MODELS,
      InventoryParametersKeys.TARGETING_DEVICE_MODELS_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: ct('AllItemsSelected', { item: ct('DeviceModels') }),
      singularLabel: ct('DeviceModel'),
      listLabel: ct('DeviceModels'),
      listKey: 'DeviceModels',
      valueKey: InventoryParametersKeys.TARGETING_DEVICE_MODELS,
      allowKey: InventoryParametersKeys.TARGETING_DEVICE_MODELS_IS_ALLOWED,
      convertResponseValue: convertDeviceModelsResponseValue,
      convertSelectedValue: convertDeviceModelsSelectedValue,
      key: InventoryParametersKeys.TARGETING_DEVICE_MODELS,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
    checkIfHidden: (publisherID) => !isPublisherSamsung(publisherID),
  },
  IPFilter: {
    key: InventoryParametersParamKeys.IP_FILTER,
    label: 'IP Filters *',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Devices',
    apiKeys: [InventoryParametersKeys.IP_FILTER],
    isSuperUserOnly: true,
    checkIfHidden: (_, isSuperUser) => !isSuperUser,
    customValidation: {
      validateKey: InventoryParametersKeys.IP_FILTER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.IP_FILTER,
      placeholder: generateGenericPlaceholder('IP Filters'),
      valueKey: InventoryParametersKeys.IP_FILTER,
      tooltip: `Match this value to the IP set in your request to Publica's endpoint. Can be comma separated.`,
    } as ITextInputWithIncludeProps,
  },
  // CONTENT
  ContentChannel: {
    key: InventoryParametersParamKeys.CONTENT_CHANNEL,
    label: 'Content Channel',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_CHANNEL,
      InventoryParametersKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_CHANNEL,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_CHANNEL,
      placeholder: generateContentPlaceholder('Content Channel'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_CHANNEL,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_CHANNEL_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentDistributor: {
    key: InventoryParametersParamKeys.CONTENT_DISTRIBUTOR,
    label: 'Content Distributor',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_DISTRIBUTOR,
      InventoryParametersKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_DISTRIBUTOR,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_DISTRIBUTOR,
      placeholder: generateContentPlaceholder('Content Distributor'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_DISTRIBUTOR,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_DISTRIBUTOR_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentGenre: {
    key: InventoryParametersParamKeys.CONTENT_GENRE,
    label: 'Content Genre',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_GENRE,
      InventoryParametersKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_GENRE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_GENRE,
      placeholder: generateContentPlaceholder('Content Genre'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_GENRE,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_GENRE_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentID: {
    key: InventoryParametersParamKeys.CONTENT_ID,
    label: 'Content ID',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_ID,
      InventoryParametersKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_ID,
      placeholder: generateContentPlaceholder('Content ID'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_ID,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_ID_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentNetwork: {
    key: InventoryParametersParamKeys.CONTENT_NETWORK,
    label: 'Content Network',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_NETWORK,
      InventoryParametersKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_NETWORK,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_NETWORK,
      placeholder: generateContentPlaceholder('Content Network'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_NETWORK,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_NETWORK_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentProducer: {
    key: InventoryParametersParamKeys.CONTENT_PRODUCER,
    label: 'Content Producer',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_PRODUCER,
      InventoryParametersKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_PRODUCER,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_PRODUCER,
      placeholder: generateContentPlaceholder('Content Producer'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_PRODUCER,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_PRODUCER_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentRating: {
    key: InventoryParametersParamKeys.CONTENT_RATING,
    label: 'Content Rating',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_RATING,
      InventoryParametersKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_RATING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_RATING,
      placeholder: generateContentPlaceholder('Content Rating'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_RATING,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_RATING_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentSeries: {
    key: InventoryParametersParamKeys.CONTENT_SERIES,
    label: 'Content Series',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_SERIES,
      InventoryParametersKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_SERIES,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_SERIES,
      placeholder: generateContentPlaceholder('Content Series'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_SERIES,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_SERIES_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentTitle: {
    key: InventoryParametersParamKeys.CONTENT_TITLE,
    label: 'Content Title',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_TITLE,
      InventoryParametersKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_TITLE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_TITLE,
      placeholder: generateContentPlaceholder('Content Title'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_TITLE,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_TITLE_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentCategory: {
    key: InventoryParametersParamKeys.CONTENT_CATEGORY,
    label: 'Content Category',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_CATEGORY,
      InventoryParametersKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_CATEGORY,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CONTENT_CATEGORY,
      placeholder: generateContentPlaceholder('Content Category'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_CATEGORY,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_CATEGORY_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  ContentLanguage: {
    key: InventoryParametersParamKeys.CONTENT_LANGUAGE,
    label: 'Content Language',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Content',
    apiKeys: [
      InventoryParametersKeys.TARGETING_CONTENT_LANGUAGE,
      InventoryParametersKeys.TARGETING_CONTENT_LANGUAGE_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_CONTENT_LANGUAGE,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: TargetingPresetParamKeys.CONTENT_LANGUAGE,
      placeholder: generateContentPlaceholder('Content Language'),
      valueKey: InventoryParametersKeys.TARGETING_CONTENT_LANGUAGE,
      allowKey: InventoryParametersKeys.TARGETING_CONTENT_LANGUAGE_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  // STREAMS
  TargetedLivestreams: {
    key: InventoryParametersParamKeys.TARGETED_LIVESTREAMS,
    label: 'Targeted Livestreams',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Streams',
    apiKeys: [InventoryParametersKeys.TARGETING_LIVESTREAM],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'All Livestreams Selected',
      singularLabel: 'Livestream',
      listLabel: 'Livestreams',
      listKey: 'Livestreams',
      valueKey: InventoryParametersKeys.TARGETING_LIVESTREAM,
      convertResponseValue: convertLivestreamResponseValue,
      convertSelectedValue: convertLivestreamSelectedValue,
      key: InventoryParametersKeys.TARGETING_LIVESTREAM,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  // App and SITE PAGES
  AppBundleIDsSitePages: {
    key: InventoryParametersParamKeys.APP_BUNDLE_IDS_SITE_PAGES,
    label: 'App Bundle IDs & Site Pages',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'App Bundle IDs & Site Pages',
    apiKeys: [
      InventoryParametersKeys.TARGETING_APP_BUNDLE_ID,
      InventoryParametersKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      InventoryParametersKeys.TARGETING_SITE_PAGES,
      InventoryParametersKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
      InventoryParametersKeys.TARGETING_APP_BUNDLE_SITE_PAGES_LOGIC,
    ],
    Component: AppBundleIDsSitePagesEditor,
    props: {
      appBundleIDKey: InventoryParametersKeys.TARGETING_APP_BUNDLE_ID,
      appBundleIDAllowedKey: InventoryParametersKeys.TARGETING_APP_BUNDLE_ID_IS_ALLOWED,
      sitePagesKey: InventoryParametersKeys.TARGETING_SITE_PAGES,
      sitePagesAllowedKey: InventoryParametersKeys.TARGETING_SITE_PAGES_IS_ALLOWED,
      logicKey: InventoryParametersKeys.TARGETING_APP_BUNDLE_SITE_PAGES_LOGIC,
    } as IAppBundleIDsSitePagesEditorProps,
  },
  // SITE DOMAINS
  SiteDomains: {
    key: InventoryParametersParamKeys.SITE_DOMAINS,
    label: 'Site Domains',
    categoryKey: InventoryParametersCategoryKeys.SUPPLY,
    selectCategoryKey: 'Site Domains',
    apiKeys: [
      InventoryParametersKeys.TARGETING_SITE_DOMAIN,
      InventoryParametersKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.TARGETING_SITE_DOMAIN,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.SITE_DOMAINS,
      placeholder: generateGenericPlaceholder('Site Domains'),
      valueKey: InventoryParametersKeys.TARGETING_SITE_DOMAIN,
      allowKey: InventoryParametersKeys.TARGETING_SITE_DOMAIN_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  // ADBREAK CATEGORY
  // Slots: {
  //   key: InventoryParametersParamKeys.SLOTS,
  //   label: 'Slots',
  //   categoryKey: InventoryParametersCategoryKeys.AD_BREAK,
  //   selectCategoryKey: 'Inventory',
  //   apiKeys: [InventoryParametersKeys.SLOTS],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     placeholder: 'All Slots Selected',
  //     singularLabel: 'Slot',
  //     listLabel: 'Slots',
  //     listKey: 'Slots',
  //     valueKey: InventoryParametersKeys.SLOTS,
  //     convertResponseValue: convertSlotsResponseValue,
  //     convertSelectedValue: convertSlotsSelectedValue,
  //     key: InventoryParametersKeys.SLOTS,
  //     disableSort: true,
  //   } as ISelectWithIncludeAndLogicProps,
  // },
  // Position: {
  //   key: InventoryParametersParamKeys.POSITION,
  //   label: 'Position',
  //   categoryKey: InventoryParametersCategoryKeys.AD_BREAK,
  //   selectCategoryKey: 'Inventory',
  //   apiKeys: [InventoryParametersKeys.POSITION],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     placeholder: 'Select Position Value',
  //     singularLabel: 'Position Value',
  //     listLabel: 'Position',
  //     listKey: 'Position',
  //     valueKey: InventoryParametersKeys.POSITION,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertStringOrNumberResponseValue,
  //     convertSelectedValue: convertPositionTargetingSelectedValue,
  //     key: InventoryParametersKeys.POSITION,
  //     disableSort: true,
  //   } as ISelectWithIncludeAndLogicProps,
  // },
  // PodSequence: {
  //   key: InventoryParametersParamKeys.POD_SEQUENCE,
  //   label: 'Pod Sequence',
  //   categoryKey: InventoryParametersCategoryKeys.AD_BREAK,
  //   selectCategoryKey: 'Inventory',
  //   apiKeys: [InventoryParametersKeys.POD_SEQUENCE],
  //   Component: SelectWithIncludeAndLogic,
  //   props: {
  //     placeholder: 'All Pod Sequence Selected',
  //     singularLabel: 'Pod Sequence',
  //     listLabel: 'Pod Sequence',
  //     listKey: 'PodSequence',
  //     valueKey: InventoryParametersKeys.POD_SEQUENCE,
  //     selectedLimit: 1,
  //     isSelectionRequired: true,
  //     convertResponseValue: convertStringOrNumberResponseValue,
  //     convertSelectedValue: convertPodSequenceTargetingSelectedValue,
  //     key: InventoryParametersKeys.POD_SEQUENCE,
  //     disableSort: true,
  //   } as ISelectWithIncludeAndLogicProps,
  // },
  // PodDuration: {
  //   key: InventoryParametersParamKeys.POD_DURATION,
  //   label: 'Pod Duration',
  //   categoryKey: InventoryParametersCategoryKeys.AD_BREAK,
  //   selectCategoryKey: 'Inventory',
  //   apiKeys: [InventoryParametersKeys.POD_DURATION],
  //   Component: PodDurationMinMax,
  //   props: {
  //     minKey: InventoryParametersKeys.POD_DURATION_MIN,
  //     maxKey: InventoryParametersKeys.POD_DURATION_MAX,
  //   } as IPodDurationMinMaxProps,
  // },
  // USERS CATEGORY
  // GEO
  TargetedRegions: {
    key: InventoryParametersParamKeys.TARGETED_REGIONS,
    label: 'Targeted Regions',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [
      InventoryParametersKeys.TARGETING_COUNTRY_GROUP_IDS,
      InventoryParametersKeys.TARGETING_COUNTRY_GROUP_IDS_IS_ALLOWED,
    ],
    Component: RegionsEditor,
  },
  TargetedCountries: {
    key: InventoryParametersParamKeys.TARGETED_COUNTRIES,
    label: 'Targeted Countries',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [
      InventoryParametersKeys.TARGETING_DEVICE_COUNTRY,
      InventoryParametersKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Countries',
      singularLabel: 'Country',
      extractStringToSort: removeEmojiPrefixFromLabel,
      listLabel: 'Countries',
      listKey: 'Countries',
      valueKey: InventoryParametersKeys.TARGETING_DEVICE_COUNTRY,
      allowKey: InventoryParametersKeys.TARGETING_DEVICE_COUNTRY_IS_ALLOWED,
      convertResponseValue: convertStringIntoArray,
      convertSelectedValue: convertSetIntoString,
      key: InventoryParametersKeys.TARGETING_DEVICE_COUNTRY,
    } as ISelectWithIncludeAndLogicProps,
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  LocalTargetingAllowed: {
    key: InventoryParametersParamKeys.LOCAL_TARGETING_ALLOWED,
    label: 'Local Targeting Included',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [InventoryParametersKeys.LOCAL_TARGETING_ALLOWED_LOGIC],
    Component: ForecastingLocalTargetingBuilder,
    props: {
      targetingCities: InventoryParametersKeys.TARGETING_ALLOWED_CITIES,
      targetingCountries: InventoryParametersKeys.TARGETING_ALLOWED_COUNTRIES,
      targetingDmas: InventoryParametersKeys.TARGETING_ALLOWED_DMAS,
      targetingStates: InventoryParametersKeys.TARGETING_ALLOWED_STATES,
      targetingZips: InventoryParametersKeys.TARGETING_ALLOWED_ZIPS,
      logicKey: InventoryParametersKeys.LOCAL_TARGETING_ALLOWED_LOGIC,
      uiKey: InventoryParametersKeys.LOCAL_TARGETING_ALLOWED_UI,
    } as IForecastingLocalTargetingBuilderProps,
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  LocalTargetingDisallowed: {
    key: InventoryParametersParamKeys.LOCAL_TARGETING_DISALLOWED,
    label: 'Local Targeting Excluded',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Geo',
    apiKeys: [InventoryParametersKeys.LOCAL_TARGETING_DISALLOWED_LOGIC],
    Component: ForecastingLocalTargetingBuilder,
    props: {
      targetingCities: InventoryParametersKeys.TARGETING_DISALLOWED_CITIES,
      targetingCountries: InventoryParametersKeys.TARGETING_DISALLOWED_COUNTRIES,
      targetingDmas: InventoryParametersKeys.TARGETING_DISALLOWED_DMAS,
      targetingStates: InventoryParametersKeys.TARGETING_DISALLOWED_STATES,
      targetingZips: InventoryParametersKeys.TARGETING_DISALLOWED_ZIPS,
      logicKey: InventoryParametersKeys.LOCAL_TARGETING_DISALLOWED_LOGIC,
      uiKey: InventoryParametersKeys.LOCAL_TARGETING_DISALLOWED_UI,
    } as IForecastingLocalTargetingBuilderProps,
  },
  // DEALS
  DealID: {
    key: InventoryParametersParamKeys.DEAL_ID,
    label: 'Deal ID',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Deals',
    apiKeys: [InventoryParametersKeys.DEAL_ID],
    customValidation: {
      validateKey: InventoryParametersKeys.DEAL_ID,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.DEAL_ID,
      placeholder: generateCustomPlaceholder('Deal IDs'),
      valueKey: InventoryParametersKeys.DEAL_ID,
    } as ITextInputWithIncludeProps,
  },
  FreewheelSectionIDs: {
    key: InventoryParametersParamKeys.FREEWHEEL_SECTION_IDS,
    label: 'Freewheel Section IDs',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Deals',
    apiKeys: [
      InventoryParametersKeys.FREEWHEEL_SECTION_IDS,
      InventoryParametersKeys.FREEWHEEL_SECTION_IDS_IS_ALLOWED,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.FREEWHEEL_SECTION_IDS,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.FREEWHEEL_SECTION_IDS,
      placeholder: generateGenericPlaceholder('Section IDs'),
      valueKey: InventoryParametersKeys.FREEWHEEL_SECTION_IDS,
      allowKey: InventoryParametersKeys.FREEWHEEL_SECTION_IDS_IS_ALLOWED,
    } as ITextInputWithIncludeProps,
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  Dayparting: {
    key: InventoryParametersParamKeys.DAYPARTING,
    label: 'Dayparting',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Time',
    apiKeys: [InventoryParametersKeys.DAYPARTING_IS_ALLOWED],
    Component: DaypartingBuilder,
    customUIConversions: {
      apiKey: InventoryParametersKeys.DAYPARTING,
      uiKey: InventoryParametersKeys.DAYPARTING_UI,
      convertResponse: parseDaypartingForUI,
      convertPayload: convertDaypartingUI,
    },
    props: { isHourOnly: true, type: 'Forecast' } as IDaypartingBuilderProps,
  },
  // SEGMENTS
  AudienceSegments: {
    key: InventoryParametersParamKeys.AUDIENCE_SEGMENTS,
    label: 'Audience Segments',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Segments',
    apiKeys: [
      InventoryParametersKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      InventoryParametersKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      InventoryParametersKeys.SEGMENT_TARGETING,
    ],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Audience Segments',
      singularLabel: 'Audience Segment',
      listLabel: 'Audience Segments',
      listKey: 'AudienceSegments',
      valueKey: InventoryParametersKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      allowKey: InventoryParametersKeys.TARGETING_AUDIENCE_SEGMENT_IDS_IS_ALLOWED,
      logicKey: InventoryParametersKeys.SEGMENT_TARGETING,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: InventoryParametersKeys.TARGETING_AUDIENCE_SEGMENT_IDS,
      tooltip: 'Choose which audience segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
  },
  ContextualSegments: {
    key: InventoryParametersParamKeys.CONTEXTUAL_SEGMENTS,
    label: 'Contextual Segments',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Segments',
    apiKeys: [InventoryParametersKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Contextual Segment',
      singularLabel: 'Contextual Segment',
      listLabel: 'Contextual Segments',
      listKey: 'ContextualSegments',
      selectedLimit: 1,
      valueKey: InventoryParametersKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS,
      enableActiveFilter: true,
      enableLabelTooltip: true,
      displayID: true,
      key: InventoryParametersKeys.TARGETING_CONTEXTUAL_SEGMENT_IDS,
      tooltip: 'Choose which contextual segment to bid on.',
    } as ISelectWithIncludeAndLogicProps,
  },
  // PRIVACY
  COPPA: {
    key: InventoryParametersParamKeys.COPPA,
    label: 'COPPA',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [InventoryParametersKeys.TARGETING_COPPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select COPPA Value',
      singularLabel: 'COPPA Value',
      listLabel: 'COPPA',
      listKey: 'COPPA',
      valueKey: InventoryParametersKeys.TARGETING_COPPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: InventoryParametersKeys.TARGETING_COPPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  LimitedAdTracking: {
    key: InventoryParametersParamKeys.LIMITED_AD_TRACKING,
    label: 'Limited Ad Tracking',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [InventoryParametersKeys.TARGETING_LIMITED_AD_TRACKING],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select Limited Ad Tracking',
      singularLabel: 'Limited Ad Tracking',
      listLabel: 'Limited Ad Tracking',
      listKey: 'LimitedAdTracking',
      valueKey: InventoryParametersKeys.TARGETING_LIMITED_AD_TRACKING,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: InventoryParametersKeys.TARGETING_LIMITED_AD_TRACKING,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  CCPA: {
    key: InventoryParametersParamKeys.CCPA,
    label: 'CCPA',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [InventoryParametersKeys.TARGETING_CCPA],
    Component: SelectWithIncludeAndLogic,
    props: {
      placeholder: 'Select CCPA Value',
      singularLabel: 'CCPA Value',
      listLabel: 'CCPA',
      listKey: 'CCPA',
      valueKey: InventoryParametersKeys.TARGETING_CCPA,
      selectedLimit: 1,
      isSelectionRequired: true,
      convertResponseValue: convertSingleNumberIntoArray,
      convertSelectedValue: convertSetIntoSingleNumber,
      key: InventoryParametersKeys.TARGETING_CCPA,
      disableSort: true,
    } as ISelectWithIncludeAndLogicProps,
  },
  // THIS IS A SPECIAL CASE AND REQUIRES CUSTOM COMPONENTS
  GDPR: {
    key: InventoryParametersParamKeys.GDPR,
    label: 'GDPR',
    categoryKey: InventoryParametersCategoryKeys.USERS,
    selectCategoryKey: 'Privacy',
    apiKeys: [
      InventoryParametersKeys.TARGETING_GDPR,
      InventoryParametersKeys.TARGETING_GDPR_CONSENT,
    ],
    Component: GDPRSelect,
    props: {},
  },
  // CUSTOMS CATEGORY
  // CUSTOM
  Custom4: {
    key: InventoryParametersParamKeys.CUSTOM_4,
    label: 'Custom 4',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_4, InventoryParametersKeys.CUSTOM_4_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_4,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_4,
      placeholder: generateCustomPlaceholder('Custom 4'),
      valueKey: InventoryParametersKeys.CUSTOM_4,
      allowKey: InventoryParametersKeys.CUSTOM_4_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom5: {
    key: InventoryParametersParamKeys.CUSTOM_5,
    label: 'Custom 5',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_5, InventoryParametersKeys.CUSTOM_5_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_5,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_5,
      placeholder: generateCustomPlaceholder('Custom 5'),
      valueKey: InventoryParametersKeys.CUSTOM_5,
      allowKey: InventoryParametersKeys.CUSTOM_5_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom6: {
    key: InventoryParametersParamKeys.CUSTOM_6,
    label: 'Custom 6',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_6, InventoryParametersKeys.CUSTOM_6_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_6,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_6,
      placeholder: generateCustomPlaceholder('Custom 6'),
      valueKey: InventoryParametersKeys.CUSTOM_6,
      allowKey: InventoryParametersKeys.CUSTOM_6_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom7: {
    key: InventoryParametersParamKeys.CUSTOM_7,
    label: 'Custom 7',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_7, InventoryParametersKeys.CUSTOM_7_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_7,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_7,
      placeholder: generateCustomPlaceholder('Custom 7'),
      valueKey: InventoryParametersKeys.CUSTOM_7,
      allowKey: InventoryParametersKeys.CUSTOM_7_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom8: {
    key: InventoryParametersParamKeys.CUSTOM_8,
    label: 'Custom 8',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_8, InventoryParametersKeys.CUSTOM_8_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_8,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_8,
      placeholder: generateCustomPlaceholder('Custom 8'),
      valueKey: InventoryParametersKeys.CUSTOM_8,
      allowKey: InventoryParametersKeys.CUSTOM_8_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom9: {
    key: InventoryParametersParamKeys.CUSTOM_9,
    label: 'Custom 9',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_9, InventoryParametersKeys.CUSTOM_9_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_9,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_9,
      placeholder: generateCustomPlaceholder('Custom 9'),
      valueKey: InventoryParametersKeys.CUSTOM_9,
      allowKey: InventoryParametersKeys.CUSTOM_9_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom10: {
    key: InventoryParametersParamKeys.CUSTOM_10,
    label: 'Custom 10',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_10, InventoryParametersKeys.CUSTOM_10_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_10,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_10,
      placeholder: generateCustomPlaceholder('Custom 10'),
      valueKey: InventoryParametersKeys.CUSTOM_10,
      allowKey: InventoryParametersKeys.CUSTOM_10_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom11: {
    key: InventoryParametersParamKeys.CUSTOM_11,
    label: 'Custom 11',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_11, InventoryParametersKeys.CUSTOM_11_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_11,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_11,
      placeholder: generateCustomPlaceholder('Custom 11'),
      valueKey: InventoryParametersKeys.CUSTOM_11,
      allowKey: InventoryParametersKeys.CUSTOM_11_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom12: {
    key: InventoryParametersParamKeys.CUSTOM_12,
    label: 'Custom 12',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_12, InventoryParametersKeys.CUSTOM_12_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_12,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_12,
      placeholder: generateCustomPlaceholder('Custom 12'),
      valueKey: InventoryParametersKeys.CUSTOM_12,
      allowKey: InventoryParametersKeys.CUSTOM_12_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom13: {
    key: InventoryParametersParamKeys.CUSTOM_13,
    label: 'Custom 13',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_13, InventoryParametersKeys.CUSTOM_13_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_13,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_13,
      placeholder: generateCustomPlaceholder('Custom 13'),
      valueKey: InventoryParametersKeys.CUSTOM_13,
      allowKey: InventoryParametersKeys.CUSTOM_13_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom14: {
    key: InventoryParametersParamKeys.CUSTOM_14,
    label: 'Custom 14',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_14, InventoryParametersKeys.CUSTOM_14_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_14,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_14,
      placeholder: generateCustomPlaceholder('Custom 14'),
      valueKey: InventoryParametersKeys.CUSTOM_14,
      allowKey: InventoryParametersKeys.CUSTOM_14_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom15: {
    key: InventoryParametersParamKeys.CUSTOM_15,
    label: 'Custom 15',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_15, InventoryParametersKeys.CUSTOM_15_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_15,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_15,
      placeholder: generateCustomPlaceholder('Custom 15'),
      valueKey: InventoryParametersKeys.CUSTOM_15,
      allowKey: InventoryParametersKeys.CUSTOM_15_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom16: {
    key: InventoryParametersParamKeys.CUSTOM_16,
    label: 'Custom 16',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_16, InventoryParametersKeys.CUSTOM_16_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_16,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_16,
      placeholder: generateCustomPlaceholder('Custom 16'),
      valueKey: InventoryParametersKeys.CUSTOM_16,
      allowKey: InventoryParametersKeys.CUSTOM_16_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom17: {
    key: InventoryParametersParamKeys.CUSTOM_17,
    label: 'Custom 17',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_17, InventoryParametersKeys.CUSTOM_17_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_17,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_17,
      placeholder: generateCustomPlaceholder('Custom 17'),
      valueKey: InventoryParametersKeys.CUSTOM_17,
      allowKey: InventoryParametersKeys.CUSTOM_17_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  Custom18: {
    key: InventoryParametersParamKeys.CUSTOM_18,
    label: 'Custom 18',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [InventoryParametersKeys.CUSTOM_18, InventoryParametersKeys.CUSTOM_18_INCLUSION],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_18,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_18,
      placeholder: generateCustomPlaceholder('Custom 18'),
      valueKey: InventoryParametersKeys.CUSTOM_18,
      allowKey: InventoryParametersKeys.CUSTOM_18_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
  CustomTargeting: {
    key: InventoryParametersParamKeys.CUSTOM_TARGETING,
    label: 'Custom Targeting',
    categoryKey: InventoryParametersCategoryKeys.CUSTOMS,
    selectCategoryKey: 'Custom',
    apiKeys: [
      InventoryParametersKeys.CUSTOM_TARGETING,
      InventoryParametersKeys.CUSTOM_TARGETING_INCLUSION,
    ],
    customValidation: {
      validateKey: InventoryParametersKeys.CUSTOM_TARGETING,
      validateFn: validateTextInput,
    },
    Component: TextInputWithInclude,
    props: {
      key: InventoryParametersParamKeys.CUSTOM_TARGETING,
      placeholder: generateCustomPlaceholder('Custom Targeting'),
      valueKey: InventoryParametersKeys.CUSTOM_TARGETING,
      allowKey: InventoryParametersKeys.CUSTOM_TARGETING_INCLUSION,
      tooltip: customTooltipMessage,
    } as ITextInputWithIncludeProps,
  },
};

export interface IInventoryParametersCategory {
  key: InventoryParametersCategory; // also acts as label
  paramKeys: InventoryParametersParamKey[];
  iconName: string;
  subText: string;
  addParamText: string;
}

export const inventoryParametersCategoriesAndParams: Record<
  InventoryParametersCategory,
  IInventoryParametersCategory
> = {
  Channels: {
    key: 'Channels',
    paramKeys: ['SiteIDs', 'ChannelGroupIDs'],
    iconName: 'channels',
    subText: 'All Channels, All Channel Groups',
    addParamText: '+ Add Parameter',
  },
  Supply: {
    key: 'Supply',
    paramKeys: [
      'TargetedDevices',
      'TargetedDeviceTypes',
      // 'TargetedDeviceSubtypes',
      // 'IPFilter',
      'ContentChannel',
      // 'ContentDistributor',
      'ContentGenre',
      'ContentID',
      'ContentNetwork',
      // 'ContentProducer',
      // 'ContentRating',
      // 'ContentSeries',
      'ContentTitle',
      // 'ContentCategory',
      // 'ContentLanguage',
      'TargetedLivestreams',
      'AppBundleIDsSitePages',
      // 'SiteDomains',
    ],
    iconName: 'cast',
    subText:
      'All Devices, All Content Params, All Stream, All Apps , All Sites Pages , All Site Domains',
    addParamText: '+ Add Parameter',
  },
  // AdBreak: {
  //   key: 'AdBreak',
  //   paramKeys: [
  //     // 'Slots',
  //     // 'Position',
  //     // 'PodSequence',
  //     // 'PodDuration'
  //   ],
  //   iconName: 'columns',
  //   subText: 'All Slots, Position, Pod Sequence, Pod Duration',
  //   addParamText: '+ Add Parameter',
  // },
  Users: {
    key: 'Users',
    paramKeys: [
      'TargetedRegions', // COUNTRY GROUPS
      'TargetedCountries',
      'LocalTargetingAllowed',
      'LocalTargetingDisallowed',
      'DealID',
      // 'FreewheelSectionIDs',
      'Dayparting',
      'AudienceSegments',
      // 'ContextualSegments',
      'COPPA',
      'LimitedAdTracking',
      // 'CCPA',
      // 'GDPR',
    ],
    iconName: 'eye',
    subText: ' All Geos, Deals, All Segments, Privacy',
    addParamText: '+ Add Parameter',
  },

  Customs: {
    key: 'Customs',
    paramKeys: [
      // 'Custom4',
      // 'Custom5',
      // 'Custom6',
      // 'Custom7',
      // 'Custom8',
      // 'Custom9',
      // 'Custom10',
      // 'Custom11',
      // 'Custom12',
      // 'Custom13',
      // 'Custom14',
      // 'Custom15',
      // 'Custom16',
      // 'Custom17',
      // 'Custom18',
      'CustomTargeting',
    ],
    iconName: 'code',
    subText: 'Custom Targeting',
    addParamText: '+ Add Parameter',
  },
};
export interface IParameterDropdown {
  key: string;
  getList: (session: ISessionState, user: IUserState) => Promise<ISelectOption[]>;
}

export const inventoryParametersDropdowns = {
  Channels: {
    key: 'Channels',
    getList: generateChannelsOptionsList,
  },
  ChannelGroups: {
    key: 'ChannelGroups',
    getList: generateChannelGroupsOptionsList,
  },
  Countries: {
    key: 'Countries',
    getList: () => generateOptionsListAsPromise(generateCountryOptions()),
  },
  CountryGroups: {
    key: 'CountryGroups',
    getList: generateCountryGroupOptionsList,
  },
  DMAs: {
    key: 'DMAs',
    getList: () => generateOptionsListAsPromise(generateDMAOptions('us')),
  },
  COPPA: {
    key: 'COPPA',
    getList: () => generateOptionsListAsPromise(TargetingCOPPAOptions),
  },
  LimitedAdTracking: {
    key: 'LimitedAdTracking',
    getList: () => generateOptionsListAsPromise(TargetBoolOptions),
  },
  CCPA: {
    key: 'CCPA',
    getList: () => generateOptionsListAsPromise(TargetingCCPAOptions),
  },
  GDPR: {
    key: 'GDPR',
    getList: () => generateOptionsListAsPromise(TargetingGDPROptions),
  },
  GDPRConsent: {
    key: 'GDPRConsent',
    getList: () => generateOptionsListAsPromise(TargetingGDPRConsentOptions),
  },
  Devices: {
    key: 'Devices',
    getList: () => generateOptionsListAsPromise(DeviceOptions),
  },
  DeviceTypes: {
    key: 'DeviceTypes',
    getList: () => generateOptionsListAsPromise(DeviceORTBTypeOptions),
  },
  DeviceSubTypes: {
    key: 'DeviceSubTypes',
    getList: generateDeviceSubTypeOptionsList,
  },
  DeviceModels: {
    key: 'DeviceModels',
    getList: generateDeviceModelsOptionsList,
  },
  Livestreams: {
    key: 'Livestreams',
    getList: () => generateOptionsListAsPromise(TargetingLiveStreamOptions),
  },
  AudienceSegments: {
    key: 'AudienceSegments',
    getList: generateAudienceSegmentOptionsList,
  },
  ContextualSegments: {
    key: 'ContextualSegments',
    getList: generateContextualSegmentOptionsList,
  },
  Slots: {
    key: 'Slots',
    getList: () => generateOptionsListAsPromise(SlotOptions),
  },
  Position: {
    key: 'Position',
    getList: () => generateOptionsListAsPromise(PositionTargetingOptions),
  },
  PodSequence: {
    key: 'PodSequence',
    getList: () => generateOptionsListAsPromise(PodSequenceTargetingOptions),
  },
} as Record<string, IParameterDropdown>;

export interface IInventoryParametersFormConfig {
  categories: Record<InventoryParametersCategory, IInventoryParametersCategory>;
  params: Record<InventoryParametersParamKey, IInventoryParametersParam>;
  dropdowns: Record<string, IParameterDropdown>;
}

export const inventoryParametersFormConfig: IInventoryParametersFormConfig = {
  categories: inventoryParametersCategoriesAndParams,
  params: InventoryParametersParams,
  dropdowns: inventoryParametersDropdowns,
};

export const generateInventoryParametersParamOptions = (
  optionKeys: InventoryParametersParamKey[],
  publisherID: string,
  isSuperUser: boolean,
) => {
  const options: any[] = [];
  let currentCategory = '' as InventoryParametersSelectCategory;
  optionKeys.forEach((key) => {
    const { label, selectCategoryKey, isSuperUserOnly, checkIfHidden } =
      InventoryParametersParams[key];
    if (selectCategoryKey && selectCategoryKey !== currentCategory) {
      currentCategory = selectCategoryKey;
      options.push({
        key: selectCategoryKey,
        label: selectCategoryKey,
        isCategory: true,
        category: selectCategoryKey,
      });
    }
    if (isSuperUserOnly && !isSuperUser) return;
    if (checkIfHidden && checkIfHidden(publisherID, isSuperUser)) return;
    options.push({
      key,
      label,
      category: selectCategoryKey,
    });
  });
  return options;
};

export const extractSuperUserOnlyInventoryParametersParamKeys =
  (): InventoryParametersParamKey[] => {
    const result: InventoryParametersParamKey[] = [];
    Object.values(InventoryParametersParams).forEach((param) => {
      if (param.isSuperUserOnly) {
        result.push(param.key);
      }
    });
    return result;
  };
